import React, { useEffect, useRef, useState, memo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import dayjs from 'dayjs';
import { useAppContext } from '../../../providers/AppProvider';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  DataZoomComponent
} from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { Card } from 'react-bootstrap';
import BucketRangeButtons from './BucketRangeButtons';
import { FallingLines } from 'react-loader-spinner';
import axios from 'axios';
import { isLiveTimeRange } from '../utils/chartUtils';
import { INFLUX_API } from '../../../config/api';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  CanvasRenderer,
  LegendComponent,
  DataZoomComponent
]);

const getDefaultOptions = (
  theme,
  getThemeColor,
  currentSpeed,
  machineSpeedParameter
) => ({
  color: [getThemeColor('primary'), getThemeColor('info')],

  tooltip: {
    trigger: 'axis',
    padding: 10,
    backgroundColor: getThemeColor('gray-100'),
    borderColor: getThemeColor('gray-300'),
    textStyle: { color: getThemeColor('dark') },
    borderWidth: 1,
    transitionDuration: 0,
    axisPointer: {
      type: 'line',
      axis: 'x'
    },
    formatter: function (params) {
      const actualSpeed = params[0].value[1];
      const parameterSpeed = params[1] ? params[1].value[1] : 'N/A';
      const time = params[0].value[0];
      return `Time: ${dayjs(time).format(
        'HH:mm:ss'
      )}<br/>Actual Speed: ${actualSpeed.toFixed(2)}<br/>Set Speed: ${
        parameterSpeed !== 'N/A' ? parameterSpeed.toFixed(2) : parameterSpeed
      }`;
    }
  },
  xAxis: {
    type: 'time',
    boundaryGap: false,
    axisLabel: {
      formatter: value => dayjs(value).format('HH:mm'),
      interval: 'auto',
      margin: 10
    },
    splitLine: { show: false }
  },
  yAxis: {
    position: 'right',
    min: 0,
    max: 250,
    axisPointer: { type: 'none' },
    axisTick: { show: false },
    splitLine: { show: false },
    axisLine: { show: false },
    axisLabel: { show: true }
  },
  series: [
    {
      name: 'Actual Speed',
      type: 'line',
      data: currentSpeed,
      showSymbol: false,
      smooth: true
    },
    {
      name: 'Set Speed',
      type: 'line',
      data: machineSpeedParameter,
      lineStyle: { type: 'dashed', width: 1, color: getThemeColor('info') },
      showSymbol: false
    }
  ],
  grid: { right: 20, left: 20, bottom: '20px', top: '2%', containLabel: true },
  animation: true,
  dataZoom: [{ type: 'inside', xAxisIndex: 0, start: 0, end: 100 }]
});

const MachineMaxSpeedAndLinearSpeed = ({
  bucket,
  dynamicBucket = false,
  machineName,
  fields,
  startTime,
  endTime
}) => {
  const chartRef = useRef(null);
  const [currentSpeed, setCurrentSpeed] = useState([]);
  const [machineSpeedParameter, setMachineSpeedParameter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentBucket, setCurrentBucket] = useState(bucket);

  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  const updateBucket = bucketName => {
    setCurrentBucket(bucketName);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${INFLUX_API}/api/influx/${machineName}/fields?fields=${fields}&start=${startTime}&end=${endTime}&bucket=${currentBucket}`
      );
      const data = response.data;

      const currentSpeedKey = data.ACT_LINE_SPEED
        ? 'ACT_LINE_SPEED'
        : data.ACT_RAMP_VALUE_PER
        ? 'ACT_RAMP_VALUE_PER'
        : data.ACT_RAMP_VALUE
        ? 'ACT_RAMP_VALUE'
        : null;
      const machineSpeedKey = data.SET_SPEED
        ? 'SET_SPEED'
        : data.MAX_RAMP_VALUE_PER
        ? 'MAX_RAMP_VALUE_PER'
        : data.MAX_RAMP_LIMIT_VALUE
        ? 'MAX_RAMP_LIMIT_VALUE'
        : null;

      const removeDuplicatesAndSort = data => {
        const sortedData = data.sort((a, b) => new Date(a[0]) - new Date(b[0]));
        return sortedData.filter(
          (item, index, array) => index === 0 || array[index - 1][0] !== item[0]
        );
      };

      setCurrentSpeed(
        currentSpeedKey ? removeDuplicatesAndSort(data[currentSpeedKey]) : []
      );
      setMachineSpeedParameter(
        machineSpeedKey ? removeDuplicatesAndSort(data[machineSpeedKey]) : []
      );
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIntervalTime = () => {
    switch (currentBucket) {
      case 'machine_data_10minute':
        return 10 * 60 * 1000;
      case 'machine_data_30minute':
        return 30 * 60 * 1000;
      case 'machine_data_hour':
        return 60 * 60 * 1000;
      default:
        return 60 * 1000;
    }
  };

  useEffect(() => {
    fetchData();
    const intervalId = setInterval(fetchData, getIntervalTime());
    return () => clearInterval(intervalId);
  }, [currentBucket, machineName, fields, startTime, endTime]);

  if (loading) {
    return (
      <Card className="h-0">
        <Card.Body style={{ height: '316px', width: '100%' }}>
          <div style={styles.spinnerContainer}>
            <FallingLines
              color={theme === 'dark' ? 'white' : '#3874ff'}
              width="100"
              visible={true}
              ariaLabel="falling-circles-loading"
            />
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="h-100">
      <Card.Body>
        <div style={styles.headerContainer}>
          <div style={styles.title}>Machine Speed Over Time</div>
          {dynamicBucket && (
            <BucketRangeButtons
              current={currentBucket}
              onChange={updateBucket}
            />
          )}
        </div>
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={getDefaultOptions(
            theme,
            getThemeColor,
            currentSpeed,
            machineSpeedParameter
          )}
          style={{ height: '316px', width: '100%' }}
        />
      </Card.Body>
    </Card>
  );
};

const styles = {
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%'
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  title: {
    fontSize: '16px',
    fontWeight: 'bold',
    // minWidth lets a long title wrap instead of shoving the resolution
    // picker out of the card: a flex item will not shrink below its
    // content width until it is told it may.
    flex: '1 1 auto',
    minWidth: 0,
    lineHeight: 1.25,
    paddingRight: '8px'
  },
};

export default memo(MachineMaxSpeedAndLinearSpeed);
